'use client';

import { Button } from '@/components/ui/button';
import { useAuthModal } from '@/contexts/auth-modal-context';
import { useAuth } from '@/hooks/useAuth';
import {
  MODE_COLOR_CLASS,
  MODE_ICONS,
  MODE_TRANSLATION_KEYS,
  PACK_MODES,
  THINKING_TO_PACK_MODE,
} from '@/lib/pack-constants';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { closeErrorModal } from '@/lib/redux/slices/errorModalSlice';
import { cn } from '@/lib/utils';
import type { CreditPack, PackMode } from '@/types/pricing';
import { AnimatePresence, m } from 'framer-motion';
import { Loader2, Package } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { use, useCallback, useMemo, useState } from 'react';

interface PackPurchaseSectionProps {
  /** Promise from the Server Component — unwrapped via React 19's `use()`. */
  packsPromise: Promise<CreditPack[]>;
}

export function PackPurchaseSection({
  packsPromise,
}: PackPurchaseSectionProps) {
  const t = useTranslations('PricingPage');
  const tMode = useTranslations('ThinkingMode');
  const locale = useLocale();
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { openSignupModal } = useAuthModal();
  const dispatch = useAppDispatch();

  const packs = use(packsPromise);

  const [buyingKey, setBuyingKey] = useState<string | null>(null);

  const currentThinkingMode = useAppSelector(
    (state) => state.uiSettings.thinkingMode
  );

  const defaultTab: PackMode = useMemo(
    () => THINKING_TO_PACK_MODE[currentThinkingMode],
    [currentThinkingMode]
  );

  const [activeMode, setActiveMode] = useState<PackMode>(defaultTab);

  // CSS-driven sliding indicator: the pill segments are equal-width grid
  // columns, so the indicator's geometry is a pure function of the active
  // index — no DOM measurement, no useLayoutEffect. The SSR markup is already
  // correct, which kills the zero-size flash on first paint (#5029, same shape
  // as #5014). The `transition-transform` still animates the smooth slide.
  const segmentCount = PACK_MODES.length;
  const activeIndex = Math.max(
    0,
    PACK_MODES.findIndex((mode) => mode === activeMode)
  );
  const segmentWidthPct = 100 / segmentCount;

  const packsByMode = useMemo(() => {
    const grouped: Record<PackMode, CreditPack[]> = {
      standard: [],
      full: [],
      deep: [],
    };
    for (const pack of packs) {
      grouped[pack.mode].push(pack);
    }
    for (const mode of PACK_MODES) {
      grouped[mode].sort((a, b) => a.size - b.size);
    }
    return grouped;
  }, [packs]);

  const handleBuyPack = useCallback(
    (pack: CreditPack) => {
      if (!isAuthenticated) {
        dispatch(closeErrorModal());
        openSignupModal('/pricing');
        return;
      }

      const key = `${pack.mode}-${pack.size}`;
      setBuyingKey(key);

      const checkoutParams = {
        pack_size: pack.size,
        thinking_mode: pack.mode,
        type: 'pack' as const,
      };
      const encoded = btoa(JSON.stringify(checkoutParams));
      router.push(`/${locale}/checkout/${encoded}`);
      // UpgradeModal auto-closes on pathname change to avoid a flash of the
      // underlying page between closing the modal and the checkout mounting.
    },
    [isAuthenticated, locale, router, dispatch, openSignupModal]
  );

  if (packs.length === 0) {
    return null;
  }

  const activePacks = packsByMode[activeMode];

  return (
    <section id='packs' aria-label={t('packSectionTitle')} className='w-full'>
      {/* Intro — the parent PricingModeToggle serves as the section header. */}
      <p className='text-center text-sm text-content-muted mb-6'>
        {t('packSubtitle')}
      </p>

      {/* Mode pill toggle + pack cards */}
      <div className='max-w-3xl mx-auto'>
        {/* Pack-mode pill (Standard / Full / Deep) — selects which mode's packs to show. */}
        <div className='flex justify-center mb-6'>
          <div
            role='group'
            aria-label={`${t('modeStandard')} / ${t('modeFull')} / ${t('modeDeep')}`}
            className='relative inline-flex rounded-full border border-line-default bg-surface-card p-1'
          >
            {/* Inner track — equal-width grid columns hosting the
                absolutely-positioned indicator, so the indicator percentages
                resolve exactly and measurement-free. */}
            <div className='relative grid grid-flow-col auto-cols-fr items-center'>
              {/* Sliding indicator — sized/positioned from the active index in
                  CSS, so it renders correctly on the SSR first paint (no
                  measurement, no zero-size flash #5029). */}
              <div
                className='absolute top-0 left-0 h-full rounded-full [background:var(--gradient-button)] shadow-sm transition-transform duration-300 ease-out'
                style={{
                  width: `${segmentWidthPct}%`,
                  transform: `translateX(${activeIndex * 100}%)`,
                }}
                aria-hidden='true'
              />

              {PACK_MODES.map((mode) => (
                <button
                  key={mode}
                  type='button'
                  aria-pressed={activeMode === mode}
                  onClick={() => setActiveMode(mode)}
                  className={cn(
                    'relative z-10 inline-flex items-center justify-center gap-1.5 px-5 py-2 text-sm font-medium rounded-full transition-colors duration-200 min-h-[44px]',
                    activeMode === mode
                      ? // #8966: text-cta-text (var(--cta-text)) is the
                        // semantic token for text on the --gradient-button
                        // indicator — solid #ffffff in all 8 palette/mode
                        // combos (same swap as the buy button above, #8795).
                        'text-cta-text'
                      : 'text-content-secondary hover:text-content-primary'
                  )}
                >
                  {MODE_ICONS[mode]}
                  {t(MODE_TRANSLATION_KEYS[mode] as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active mode description — reuses ThinkingMode translations */}
        <AnimatePresence mode='wait'>
          <m.p
            key={`desc-${activeMode}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className='text-center text-sm text-content-muted mb-6'
          >
            {activeMode === 'standard' && tMode('fastDescription')}
            {activeMode === 'full' && tMode('thinkingDescription')}
            {activeMode === 'deep' && tMode('deepThinkingDescription')}
          </m.p>
        </AnimatePresence>

        {/* Pack cards for active mode — cross-fade on mode switch */}
        <AnimatePresence mode='wait'>
          <m.div
            key={activeMode}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className='grid grid-cols-1 sm:grid-cols-3 gap-6'
          >
            {activePacks.map((pack, packIndex) => {
              const isBestValue =
                packIndex === activePacks.length - 1 && activePacks.length > 1;
              const packKey = `${pack.mode}-${pack.size}`;
              const isBuying = buyingKey === packKey;

              return (
                <div
                  key={packKey}
                  className={cn(
                    'relative bg-surface-card rounded-xl border border-line-default',
                    'flex flex-col items-center p-6 text-center',
                    'transition-shadow hover:shadow-md'
                  )}
                >
                  {/* Best value badge for largest pack */}
                  {isBestValue && (
                    <div className='absolute -top-3 left-1/2 -translate-x-1/2'>
                      <span className='bg-status-success-text text-surface-card text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap'>
                        {t('bestValue')}
                      </span>
                    </div>
                  )}

                  {/* Pack icon */}
                  <Package
                    className={cn('h-8 w-8 mb-3', MODE_COLOR_CLASS[activeMode])}
                    aria-hidden='true'
                  />

                  {/* Pack size */}
                  <h3 className='text-xl font-bold text-content-primary'>
                    {pack.size}{' '}
                    <span className='text-base font-medium text-content-secondary'>
                      {pack.size === 1
                        ? t('packTaskSingular')
                        : t('packTaskPlural')}
                    </span>
                  </h3>

                  {/* Total price */}
                  <p className='mt-2 text-2xl font-bold text-content-primary'>
                    {pack.price_eur % 1 === 0
                      ? `${pack.price_eur.toFixed(0)} EUR`
                      : `${pack.price_eur.toFixed(2).replace('.', ',')} EUR`}
                  </p>

                  {/* Per-query price */}
                  <p className='text-sm text-content-muted'>
                    {pack.per_task_eur.toFixed(2).replace('.', ',')} EUR /{' '}
                    {t('packTaskSingular')}
                  </p>

                  {/* Buy button */}
                  <Button
                    onClick={() => handleBuyPack(pack)}
                    disabled={buyingKey !== null}
                    className={cn(
                      'mt-4 w-full min-h-[44px]',
                      '[background:var(--gradient-button)]',
                      'hover:opacity-90',
                      // #8795: was text-content-inverse — near-black
                      // (#141413) in the default/calm/warm dark palettes
                      // against this gradient (2.85-3.41:1, fails AA);
                      // text-cta-text resolves to solid #ffffff in all 8
                      // palette/mode combos and clears AA everywhere.
                      'text-cta-text font-semibold',
                      'rounded-lg transition-all duration-300',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    {isBuying ? (
                      <span className='flex items-center gap-2'>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        {t('packProcessing')}
                      </span>
                    ) : (
                      t('buy')
                    )}
                  </Button>
                </div>
              );
            })}
          </m.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
